import React, { forwardRef } from 'react';

import { Select } from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

import { AlineTooltip } from './AlineTooltip';
import { BackArrowIcon } from '../icons/BackArrowIcon';

export const panelHeaderHeightPx = 48;

const useStyles = makeStyles<{ height?: number }>()((theme, { height }) => ({
  backButton: {
    marginRight: theme.spacing(1),
  },
  headerBar: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `solid 1px ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
    // Some components rely on this height for proper CSS (scroll containers) so change with care.
    maxHeight: height ?? panelHeaderHeightPx,
    minHeight: height ?? panelHeaderHeightPx,
    padding: theme.spacing(1, 1.5),
  },
}));

interface PanelHeaderProps extends React.HTMLAttributes<HTMLElement> {
  backButtonTooltip?: string;
  className?: string;
  height?: number;
  onClickBack?: (event: React.MouseEvent) => void;
  showBackButton?: boolean;
  text?: string;
}

export const PanelHeader = forwardRef(function PanelHeader(
  props: PanelHeaderProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const {
    backButtonTooltip,
    children,
    className,
    height,
    onClickBack,
    showBackButton = false,
    text,
  } = props;
  const { classes } = useStyles({ height });

  return (
    <div ref={ref} className={clsx(className, classes.headerBar)}>
      {showBackButton && (
        <AlineTooltip arrow placement="top" title={backButtonTooltip ?? ''}>
          <IconButton
            className={classes.backButton}
            data-cy={Select.document.editor.rightDrawer.goBackButton}
            size="small"
            onClick={onClickBack}
          >
            <BackArrowIcon />
          </IconButton>
        </AlineTooltip>
      )}
      {text && (
        <Typography noWrap variant="subtitle1">
          {text}
        </Typography>
      )}
      {children}
    </div>
  );
});
